/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAssetsRequest,
  ListAssetsSecurity,
} from "../models/operations/listassets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListAssetsQuery,
  ListAssetsQueryData,
  prefetchListAssets,
  queryKeyListAssets,
} from "./listAssets.core.js";
export {
  buildListAssetsQuery,
  type ListAssetsQueryData,
  prefetchListAssets,
  queryKeyListAssets,
};

export type ListAssetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAssets assets
 *
 * @remarks
 * List all assets for a project.
 */
export function useListAssets(
  request?: ListAssetsRequest | undefined,
  security?: ListAssetsSecurity | undefined,
  options?: QueryHookOptions<ListAssetsQueryData, ListAssetsQueryError>,
): UseQueryResult<ListAssetsQueryData, ListAssetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListAssetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAssets assets
 *
 * @remarks
 * List all assets for a project.
 */
export function useListAssetsSuspense(
  request?: ListAssetsRequest | undefined,
  security?: ListAssetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<ListAssetsQueryData, ListAssetsQueryError>,
): UseSuspenseQueryResult<ListAssetsQueryData, ListAssetsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListAssetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListAssetsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: ListAssetsQueryData,
): ListAssetsQueryData | undefined {
  const key = queryKeyListAssets(...queryKeyBase);

  return client.setQueryData<ListAssetsQueryData>(key, data);
}

export function invalidateListAssets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "assets", "listAssets", ...queryKeyBase],
  });
}

export function invalidateAllListAssets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "assets", "listAssets"],
  });
}
